import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";

import { CollegeType } from "../../../../core/utils";
import { useStudent } from "../../../../redux/Student/hooks";

const enrollmentStatusDirectory = {
  ENROLLMENT_1_INTERESTED: {
    present: "Search Schools",
    past: "Searched Schools"
  },
  ENROLLMENT_2_APPLIED: {
    present: "Apply",
    past: "Applied"
  },
  ENROLLMENT_3_ACCEPTED: {
    present: "Get Accepted",
    past: "Got Accepted"
  },
  ENROLLMENT_4_UPLOADED_LETTER: {
    present: "Upload an Award Letter",
    past: "Uploaded Award Letter"
  },
  ENROLLMENT_5_ENROLLED: {
    present: "Enroll",
    past: "Enrolled"
  }
};

function ProgressBar({ isDesktop }) {
  const { student } = useStudent();
  const [percent, setPercent] = useState(0);
  const [localStatus, setLocalStatus] = useState([]);
  const [linesPositions, setLinesPositions] = useState([]);

  const getEnrollmentStatus = useCallback(() => {
    const chosenSchoolsYearType =
      student?.chosenSchoolsYearType || CollegeType.FOUR_YEARS;
    const status =
      chosenSchoolsYearType === CollegeType.TWO_YEARS
        ? student?.twoYearEnrollmentStatus
        : student?.fourYearEnrollmentStatus;

    if (status === "ENROLLMENT_0_DENIED") return "";

    if (
      status === "ENROLLMENT_21_DEFERRED" ||
      status === "ENROLLMENT_22_WAITLISTED"
    ) {
      return "ENROLLMENT_2_APPLIED";
    }

    return status;
  }, [
    student?.chosenSchoolsYearType,
    student?.twoYearEnrollmentStatus,
    student?.fourYearEnrollmentStatus
  ]);

  useEffect(() => {
    const chosenSchoolsYearType =
      student?.chosenSchoolsYearType || CollegeType.FOUR_YEARS;
    const status =
      chosenSchoolsYearType === CollegeType.TWO_YEARS
        ? student?.twoYearEnrollmentStatus
        : student?.fourYearEnrollmentStatus;

    if (status === "ENROLLMENT_0_DENIED") {
      setPercent(0);
    }
  }, [
    student?.twoYearEnrollmentStatus,
    student?.fourYearEnrollmentStatus,
    student?.chosenSchoolsYearType
  ]);

  const updateLinesPositions = useCallback(() => {
    const linesPositions = [];
    const localStatusFiltered = localStatus.filter(
      (status) => typeof status.id !== "number"
    );
    const containerPosition = document
      .getElementById("status-container")
      ?.getBoundingClientRect();

    if (!containerPosition) return;

    const chosenSchoolsYearType =
      student?.chosenSchoolsYearType || CollegeType.FOUR_YEARS;
    const currentStatus =
      chosenSchoolsYearType === CollegeType.TWO_YEARS
        ? student?.twoYearEnrollmentStatus
        : student?.fourYearEnrollmentStatus;

    if (currentStatus === "ENROLLMENT_0_DENIED") {
      setPercent(0);
      return;
    }

    localStatusFiltered.forEach((status, index) => {
      const labelPosition = document
        .getElementById(`status-${status.id}`)
        ?.getBoundingClientRect();

      if (!labelPosition) return;

      const position = labelPosition.x - containerPosition.x;
      linesPositions.push({
        left: position + labelPosition.width / 2
      });

      if (status.active) {
        if (isDesktop)
          setPercent(
            ((position + labelPosition.width + 8) * 100) /
              containerPosition.width
          );
        else setPercent(((index + 0.4) * 100) / 4);
      }
    });
    setLinesPositions(linesPositions);
  }, [
    isDesktop,
    localStatus,
    student?.chosenSchoolsYearType,
    student?.twoYearEnrollmentStatus,
    student?.fourYearEnrollmentStatus
  ]);

  useEffect(() => {
    const computedStatus = [];
    const currentStatus = getEnrollmentStatus();

    const statusItems = Object.entries(enrollmentStatusDirectory).map(
      ([id, name]) => ({
        id,
        name
      })
    );

    const statusConnectors = [
      { description: "Identify your fit factors" },
      { description: "Fill out your Fin Aid forms" },
      { description: "Compare your options" }
    ];

    statusItems.forEach((statusItem, index) => {
      const isActive = statusItem.id <= currentStatus;
      computedStatus.push({
        id: statusItem.id,
        active: isActive,
        label: (
          <div className="w-24 text-center tablet:text-sm tablet:w-auto">
            {isActive
              ? enrollmentStatusDirectory[statusItem.id].past
              : enrollmentStatusDirectory[statusItem.id].present}
          </div>
        )
      });

      if (index !== statusItems.length - 1) {
        let conector = {};
        if (index <= 2) {
          conector = statusConnectors[index];
        } else {
          conector = statusConnectors[statusConnectors.length - 1];
        }
        computedStatus.push({
          id: index,
          active: false,
          label: (
            <div className="w-20 text-xs text-center text-gray-600">
              {conector.description}
            </div>
          )
        });
      }
    });

    setLocalStatus(computedStatus);
  }, [getEnrollmentStatus]);

  useEffect(() => {
    updateLinesPositions();

    const timeoutId = setTimeout(updateLinesPositions, 100);

    window.addEventListener("resize", updateLinesPositions);

    return () => {
      window.removeEventListener("resize", updateLinesPositions);
      clearTimeout(timeoutId);
    };
  }, [updateLinesPositions, student]);

  return (
    <div className="mt-4 w-full bg-white">
      <div className="p-4 flex flex-1 flex-col">
        <div
          id="my-path-container"
          className="flex flex-1 flex-row items-center"
        >
          <p className="text-3xl font-bold">{student.firstName}'s Path</p>
        </div>
        <div
          className="
            h-8
            mt-4
            flex
            flex-1
            flex-row
            bg-white
            text-white
            rounded-full
            shadow-black
            mobile:h-6
            mobile-sm:h-6
          "
        >
          <div
            style={{ width: `${percent}%` }}
            className="
              h-8
              rounded-full
              bg-primary-green
              mobile:h-6
              mobile-sm:h-6
            "
          ></div>
        </div>
        <div className="tablet:hidden laptop:hidden desktop:hidden">
          <p className="my-3 text-sm font-bold">
            {getEnrollmentStatus() === "ENROLLMENT_4_UPLOADED_LETTER" ||
            getEnrollmentStatus() === "ENROLLMENT_5_ENROLLED"
              ? "You are doing great! You have uploaded award letters!"
              : ""}
          </p>
        </div>
        <div className="mobile:hidden mobile-sm:hidden tablet-sm:hidden">
          <div className="h-5 px-2 w-full relative">
            {localStatus
              .filter((status) => typeof status.id !== "number")
              .map((status, index) => (
                <div
                  key={`status-line-${status.id}`}
                  className="h-5 border-r absolute border-primary-green"
                  style={{ left: `${linesPositions[index]?.left || 0}px` }}
                ></div>
              ))}
          </div>
          <div
            id="status-container"
            className="px-2 flex flex-1 flex-row justify-between"
          >
            {localStatus.map((status) => (
              <div
                id={`status-${status.id}`}
                key={`status-${status.id}`}
                className={`
                  font-semibold
                  ${status.active ? "text-primary-green" : "text-black"}
                `}
              >
                {status.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

ProgressBar.propTypes = {
  isDesktop: PropTypes.bool
};

export default ProgressBar;
